import { useEffect, useState } from "react";
import axios from "axios";
import { Check, Clock3, RefreshCcw, ShieldX, WifiOff } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { clearPairedDevice, loadPairedDevice, savePairedDevice } from "@/lib/pairedDevice";

const API_BASE_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const API = `${API_BASE_URL}/api`;

function formatTimestamp(value) {
  if (!value) return "n/a";
  try {
    return new Date(value).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return value;
  }
}

function secondsRemaining(expiresAt) {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 1000));
}

function stateMeta(state) {
  switch (state) {
    case "waiting":
      return {
        title: "Approval waiting",
        body: "The terminal is paused on your Windows laptop. Approve or reject to continue.",
        color: "#F59E0B",
        icon: Clock3,
      };
    case "submitting":
      return {
        title: "Sending decision",
        body: "Runzr is submitting your choice to the laptop.",
        color: "#FFFFFF",
        icon: RefreshCcw,
      };
    case "approved_pending_consume":
      return {
        title: "Approved",
        body: "Decision sent. Waiting for the laptop process to resume.",
        color: "#10B981",
        icon: Check,
      };
    case "rejected_pending_consume":
      return {
        title: "Rejected",
        body: "Decision sent. Waiting for the laptop process to update.",
        color: "#EF4444",
        icon: ShieldX,
      };
    case "timed_out":
      return {
        title: "Approval expired",
        body: "The checkpoint timed out before a decision was consumed.",
        color: "#EF4444",
        icon: Clock3,
      };
    case "already_resolved":
      return {
        title: "Already resolved",
        body: "This approval was already handled. You can wait for the next one.",
        color: "#A1A1AA",
        icon: Check,
      };
    case "server_unreachable":
      return {
        title: "Server unreachable",
        body: "The local Runzr server is not responding right now.",
        color: "#EF4444",
        icon: WifiOff,
      };
    case "no_approval":
    default:
      return {
        title: "No approval waiting",
        body: "Open this page when your Windows terminal hits a Runzr checkpoint.",
        color: "#A1A1AA",
        icon: Clock3,
      };
  }
}

export default function ApprovePage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState({
    view_state: "no_approval",
    session: null,
    approval: null,
  });
  const [pairedDevice, setPairedDevice] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [notificationPermission, setNotificationPermission] = useState(
    typeof window !== "undefined" && "Notification" in window ? window.Notification.permission : "unsupported",
  );
  const [lastNotifiedApprovalId, setLastNotifiedApprovalId] = useState(null);
  const queryDeviceKey = (searchParams.get("device") || "").trim();
  const queryDeviceName = (searchParams.get("name") || "").trim();
  const useLocalMode = !queryDeviceKey && !pairedDevice && typeof window !== "undefined" && Boolean(window.location.port);
  const deviceKey = queryDeviceKey || pairedDevice?.deviceKey || "";
  const deviceName = queryDeviceName || pairedDevice?.deviceName || "";
  const approvalEndpoint = deviceKey
    ? `${API}/relay/approval?device_key=${encodeURIComponent(deviceKey)}`
    : useLocalMode
      ? `${API}/local/approval`
      : null;
  const decisionEndpoint = deviceKey
    ? `${API}/relay/approval/decision`
    : useLocalMode
      ? `${API}/local/approval/decision`
      : null;

  useEffect(() => {
    document.title = "Runzr - Approval";
  }, [deviceKey]);

  useEffect(() => {
    if (queryDeviceKey.length >= 8) {
      const existing = loadPairedDevice();
      setPairedDevice(savePairedDevice(queryDeviceKey, queryDeviceName || existing?.deviceName));
      return;
    }

    setPairedDevice(loadPairedDevice());
  }, [queryDeviceKey, queryDeviceName]);

  useEffect(() => {
    if (!approvalEndpoint) {
      setStatus({
        view_state: "no_approval",
        session: null,
        approval: null,
      });
      setErrorMessage("");
      return undefined;
    }

    let active = true;

    const load = async () => {
      try {
        const response = await axios.get(approvalEndpoint);
        if (!active) return;
        setStatus(response.data);
        setErrorMessage("");
      } catch (error) {
        if (!active) return;
        setStatus((current) => ({ ...current, view_state: "server_unreachable" }));
        setErrorMessage(error.response?.data?.detail || "Could not reach the local Runzr server.");
      }
    };

    load();
    const interval = setInterval(load, 2000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [approvalEndpoint]);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !("Notification" in window) ||
      notificationPermission !== "granted" ||
      status.view_state !== "waiting" ||
      !status.approval?.id ||
      status.approval.id === lastNotifiedApprovalId
    ) {
      return;
    }

    const title = status.approval.title || "Runzr approval waiting";
    const body = status.approval.command_preview || status.approval.body || "Open Runzr to respond.";
    const notification = new window.Notification(title, { body });
    notification.onclick = () => window.focus();
    setLastNotifiedApprovalId(status.approval.id);
  }, [lastNotifiedApprovalId, notificationPermission, status]);

  const enableNotifications = async () => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    const permission = await window.Notification.requestPermission();
    setNotificationPermission(permission);
  };

  const submitDecision = async (decision) => {
    if (!decisionEndpoint) return;
    setIsSubmitting(true);
    setStatus((current) => ({ ...current, view_state: "submitting" }));
    setErrorMessage("");
    try {
      const payload = deviceKey ? { decision, device_key: deviceKey } : { decision };
      const response = await axios.post(decisionEndpoint, payload);
      setStatus(response.data);
    } catch (error) {
      setErrorMessage(error.response?.data?.detail || "Failed to submit decision.");
      setStatus((current) => ({
        ...current,
        view_state: current.approval?.status === "pending" ? "waiting" : current.view_state,
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const meta = stateMeta(status.view_state);
  const Icon = meta.icon;
  const approval = status.approval;
  const session = status.session;
  const remaining = approval ? secondsRemaining(approval.timeout_at || approval.expires_at) : null;
  const canAct = status.view_state === "waiting" && !isSubmitting;
  const needsConnection = !deviceKey && !useLocalMode;
  const switchDevice = () => {
    clearPairedDevice();
    setPairedDevice(null);
    setStatus({
      view_state: "no_approval",
      session: null,
      approval: null,
    });
    setErrorMessage("");
  };

  return (
    <div
      className="min-h-screen px-4 py-6 sm:px-6"
      style={{
        background:
          "radial-gradient(circle at top, rgba(245,158,11,0.14), transparent 28%), #0A0A0A",
      }}
    >
      <div className="mx-auto w-full max-w-md">
        <div className="mb-5 flex items-center justify-between">
          <div>
            <p
              className="text-xs uppercase tracking-[0.32em]"
              style={{ color: "#71717A", fontFamily: "'JetBrains Mono', monospace" }}
            >
              Runzr
            </p>
            <h1 className="mt-2 text-3xl font-semibold text-white">Approval</h1>
            {deviceKey ? (
              <p className="mt-2 text-xs" style={{ color: "#71717A", fontFamily: "'JetBrains Mono', monospace" }}>
                device={deviceKey.slice(0, 8)}...
              </p>
            ) : null}
            {deviceName ? (
              <div className="mt-2 flex items-center gap-3">
                <p className="text-sm" style={{ color: "#A1A1AA" }}>
                  Connected to {deviceName}
                </p>
                {!queryDeviceKey && !useLocalMode ? (
                  <button
                    type="button"
                    onClick={switchDevice}
                    className="text-xs underline underline-offset-4"
                    style={{ color: "#71717A" }}
                  >
                    Switch device
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="flex items-center gap-2 rounded-full px-4 py-2 text-sm"
            style={{
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "#FFFFFF",
            }}
          >
            <RefreshCcw className="h-4 w-4" />
            Refresh
          </button>
        </div>

        <div
          className="rounded-[28px] p-5 shadow-2xl"
          style={{
            background: "rgba(18,18,18,0.96)",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <div className="flex items-start gap-3">
            <div
              className="mt-1 rounded-2xl p-3"
              style={{ background: `${meta.color}22`, color: meta.color }}
            >
              <Icon className={`h-5 w-5 ${status.view_state === "submitting" ? "animate-spin" : ""}`} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-lg font-semibold text-white">{meta.title}</p>
              <p className="mt-1 text-sm leading-6" style={{ color: "#A1A1AA" }}>
                {meta.body}
              </p>
            </div>
          </div>

          {notificationPermission !== "granted" && notificationPermission !== "unsupported" ? (
            <div
              className="mt-4 flex items-center justify-between gap-3 rounded-2xl p-4 text-sm"
              style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
            >
              <p style={{ color: "#D4D4D8" }}>Enable browser notifications on this phone for faster approval alerts.</p>
              <button
                type="button"
                onClick={enableNotifications}
                className="rounded-full px-3 py-2 text-xs font-medium"
                style={{ background: "#FFFFFF", color: "#090909" }}
              >
                Enable
              </button>
            </div>
          ) : null}

          {needsConnection ? (
            <div className="mt-5 space-y-4">
              <section
                className="rounded-2xl p-4"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
              >
                <p className="text-sm font-medium text-white">No laptop connected</p>
                <p className="mt-2 text-sm leading-6" style={{ color: "#A1A1AA" }}>
                  Open the one-time connect link from your Windows laptop first. After that, this page can be reused
                  for future approvals.
                </p>
              </section>

              <Link
                to="/connect"
                className="block rounded-2xl px-4 py-4 text-center text-sm font-medium"
                style={{ background: "#FFFFFF", color: "#090909" }}
              >
                Open connect page
              </Link>
            </div>
          ) : approval ? (
            <div className="mt-5 space-y-4">
              <section
                className="rounded-2xl p-4"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-medium text-white">{approval.title}</p>
                  {remaining !== null && status.view_state === "waiting" ? (
                    <span
                      className="shrink-0 rounded-full px-2.5 py-1 text-xs"
                      style={{
                        background: "rgba(245,158,11,0.12)",
                        color: "#F59E0B",
                        fontFamily: "'JetBrains Mono', monospace",
                      }}
                    >
                      {remaining}s
                    </span>
                  ) : null}
                </div>
                {approval.body ? (
                  <p className="mt-3 text-sm leading-6" style={{ color: "#D4D4D8" }}>
                    {approval.body}
                  </p>
                ) : null}
              </section>

              <section
                className="rounded-2xl p-4"
                style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}
              >
                <div className="space-y-3 text-sm">
                  <div>
                    <p style={{ color: "#71717A" }}>Session</p>
                    <p className="mt-1 font-medium text-white">{session?.title || "Current session"}</p>
                  </div>
                  <div>
                    <p style={{ color: "#71717A" }}>Command preview</p>
                    <pre
                      className="mt-1 overflow-x-auto whitespace-pre-wrap break-words rounded-xl p-3 text-xs"
                      style={{
                        background: "#090909",
                        color: "#E4E4E7",
                        fontFamily: "'JetBrains Mono', monospace",
                        border: "1px solid rgba(255,255,255,0.08)",
                      }}
                    >
                      {approval.command_preview || session?.command_preview || "n/a"}
                    </pre>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p style={{ color: "#71717A" }}>Started</p>
                      <p className="mt-1 text-white">{formatTimestamp(session?.started_at)}</p>
                    </div>
                    <div>
                      <p style={{ color: "#71717A" }}>State</p>
                      <p className="mt-1 text-white">{status.view_state.replaceAll("_", " ")}</p>
                    </div>
                  </div>
                </div>
              </section>

              <div className="sticky bottom-0 z-10 -mx-5 border-t px-5 pb-1 pt-4" style={{ background: "rgba(18,18,18,0.98)", borderColor: "rgba(255,255,255,0.08)" }}>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    disabled={!canAct}
                    onClick={() => submitDecision("reject")}
                    className="rounded-2xl px-4 py-4 text-sm font-medium transition-opacity"
                    style={{
                      background: "rgba(239,68,68,0.12)",
                      color: "#FCA5A5",
                      border: "1px solid rgba(239,68,68,0.18)",
                      opacity: canAct ? 1 : 0.55,
                    }}
                  >
                    Reject
                  </button>
                  <button
                    type="button"
                    disabled={!canAct}
                    onClick={() => submitDecision("approve")}
                    className="rounded-2xl px-4 py-4 text-sm font-medium transition-opacity"
                    style={{
                      background: "#FFFFFF",
                      color: "#090909",
                      opacity: canAct ? 1 : 0.55,
                    }}
                  >
                    Approve
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div
              className="mt-5 rounded-2xl p-4 text-sm"
              style={{ background: "rgba(255,255,255,0.03)", color: "#A1A1AA" }}
            >
              {useLocalMode
                ? "Start the local server, then run a workflow through Runzr on the laptop."
                : "No approval is waiting for this connected laptop right now."}
              {!useLocalMode && !queryDeviceKey ? (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={switchDevice}
                    className="text-xs underline underline-offset-4"
                    style={{ color: "#D4D4D8" }}
                  >
                    Pair a different device
                  </button>
                </div>
              ) : null}
            </div>
          )}

          {errorMessage ? (
            <div
              className="mt-4 rounded-2xl p-4 text-sm"
              style={{
                background: "rgba(239,68,68,0.12)",
                border: "1px solid rgba(239,68,68,0.16)",
                color: "#FCA5A5",
              }}
            >
              {errorMessage}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
